import React, { useState, useEffect, useMemo } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import azureDevOpsService from './services/azureDevOpsService';
import { getSprintPeriods, getCurrentSprintIndex } from './utils/sprintUtils';

// Même référentiel que les KPI bugs TA : le sprint 23 commence le mer. 5 nov. 2025
const SPRINT_BASE_START = new Date('2025-11-05T00:00:00');
const SPRINT_BASE_NUMBER = 23;
const DAY_MS = 24 * 60 * 60 * 1000;

const SPRINT_COUNTS = [4, 8, 12];

const TYPE_FILTERS = [
  { key: 'all', label: 'Tous' },
  { key: 'Product Backlog Item', label: 'PBI' },
  { key: 'Bug', label: 'Bugs' },
];

function isoDay(d) {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().split('T')[0];
}

function shortDate(d) {
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

function mean(arr) {
  return arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : null;
}

function median(arr) {
  if (!arr.length) return null;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function round1(v) {
  return v == null ? null : Math.round(v * 10) / 10;
}

function fmt(v) {
  return v == null ? '–' : v.toFixed(1);
}

function buildSprints(count) {
  const currentIndex = getCurrentSprintIndex(SPRINT_BASE_START);
  const firstIndex = Math.max(0, currentIndex - (count - 1));
  return getSprintPeriods(SPRINT_BASE_START, firstIndex, count, SPRINT_BASE_NUMBER).map(s => ({
    ...s,
    // fin exclusive : début du sprint suivant (le `end` de sprintUtils est à minuit du dernier jour)
    endExclusive: new Date(s.start.getTime() + 14 * DAY_MS),
    current: Date.now() >= s.start.getTime() && Date.now() < s.start.getTime() + 14 * DAY_MS,
  }));
}

export default function KpiTimeInTestBySprint() {
  const [sprintCount, setSprintCount] = useState(8);
  const [sampleSize, setSampleSize] = useState(50);
  const [typeFilter, setTypeFilter] = useState('all');
  const [squadFilter, setSquadFilter] = useState('all');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);

  async function calculate(count = sprintCount) {
    setLoading(true);
    setError(null);
    setProgress(null);
    try {
      const sprints = buildSprints(count);
      const rangeStart = isoDay(sprints[0].start);
      const rangeEnd = isoDay(new Date(Math.min(Date.now(), sprints[sprints.length - 1].end.getTime())));
      const eligible = await azureDevOpsService.getTestEligibleTickets(rangeStart, rangeEnd);

      // Répartition par sprint (date d'arrivée en 4- Doc ou au-delà), puis
      // échantillon des `sampleSize` tickets les plus récents de chaque sprint
      const sprintOf = {};
      const sprintRows = sprints.map(sprint => {
        const inSprint = eligible.filter(t =>
          t.stateChangeDate && t.stateChangeDate >= sprint.start && t.stateChangeDate < sprint.endExclusive
        );
        const sampled = inSprint.slice(0, sampleSize);
        for (const t of sampled) sprintOf[t.id] = sprint.name;
        return { ...sprint, eligible: inSprint.length };
      });

      const ids = Object.keys(sprintOf).map(Number);
      const { tickets, failed } = await azureDevOpsService.computeTestStateDurations(ids, (done, total) =>
        setProgress({ done, total })
      );

      setResult({
        sprints: sprintRows,
        tickets: tickets.map(t => ({ ...t, sprint: sprintOf[t.id] })),
        failed,
        sampleSize,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { calculate(); }, []);

  const squads = useMemo(
    () => [...new Set((result?.tickets ?? []).map(t => t.squad))].sort((a, b) => {
      if (a === 'Autres') return 1;
      if (b === 'Autres') return -1;
      return a.localeCompare(b);
    }),
    [result]
  );

  const rows = useMemo(() => {
    if (!result) return [];
    const filtered = result.tickets.filter(t =>
      (typeFilter === 'all' || t.type === typeFilter) && (squadFilter === 'all' || t.squad === squadFilter)
    );
    return result.sprints.map(sprint => {
      const days = filtered.filter(t => t.sprint === sprint.name).map(t => t.days);
      return {
        sprint: sprint.name,
        label: sprint.current ? `${sprint.name} (en cours)` : sprint.name,
        period: `${shortDate(sprint.start)} → ${shortDate(sprint.end)}`,
        eligible: sprint.eligible,
        count: days.length,
        average: round1(mean(days)),
        median: round1(median(days)),
      };
    });
  }, [result, typeFilter, squadFilter]);

  const allDays = rows.length
    ? result.tickets
        .filter(t => (typeFilter === 'all' || t.type === typeFilter) && (squadFilter === 'all' || t.squad === squadFilter))
        .map(t => t.days)
    : [];

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Temps moyen passé en Test par sprint</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          PBI et Bugs · Tribu Argonos · temps cumulé dans l'état 3- Test (jours calendaires) ·
          tickets rattachés au sprint où ils sont arrivés en 4- Doc ou au-delà
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Sprints
          </label>
          <select
            value={sprintCount}
            onChange={e => setSprintCount(Number(e.target.value))}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {SPRINT_COUNTS.map(n => <option key={n} value={n}>{n} derniers</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Échantillon / sprint <span className="font-normal normal-case text-gray-400">(min 50)</span>
          </label>
          <input
            type="number"
            value={sampleSize}
            min="50"
            max="300"
            step="10"
            onChange={e => setSampleSize(Math.max(50, Math.min(300, Number(e.target.value) || 50)))}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-24 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => calculate()}
          disabled={loading}
          className="px-5 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
        >
          {loading ? 'Calcul…' : 'Calculer'}
        </button>
        <div className="ml-auto flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Squad
            </label>
            <select
              value={squadFilter}
              onChange={e => setSquadFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Toutes les squads</option>
              {squads.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex rounded-md border border-gray-300 overflow-hidden">
            {TYPE_FILTERS.map(f => (
              <button
                key={f.key}
                onClick={() => setTypeFilter(f.key)}
                className={'px-3 py-1.5 text-sm font-medium transition-colors ' +
                  (typeFilter === f.key ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-100')}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          Erreur : {error}
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center h-36 text-gray-400 gap-3">
          <svg className="animate-spin h-7 w-7 text-blue-500" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm">
            {progress
              ? `Lecture de l'historique des tickets… ${progress.done} / ${progress.total}`
              : 'Recherche des tickets par sprint…'}
          </span>
        </div>
      )}

      {result && !loading && (
        <>
          {result.failed > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 rounded-lg mb-4 text-sm">
              L'historique de {result.failed} tickets n'a pas pu être lu ; ils sont exclus du calcul.
            </div>
          )}

          {/* KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-xl border-2 border-blue-200">
              <div className="text-xs font-semibold text-blue-500 uppercase tracking-wide mb-1">
                Moyenne sur les {rows.length} sprints
              </div>
              <div className="text-5xl font-bold text-blue-700 leading-none">{fmt(mean(allDays))}</div>
              <div className="text-sm text-blue-500 mt-2">jours</div>
            </div>
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-5 rounded-xl border-2 border-indigo-200">
              <div className="text-xs font-semibold text-indigo-500 uppercase tracking-wide mb-1">
                Médiane
              </div>
              <div className="text-5xl font-bold text-indigo-700 leading-none">{fmt(median(allDays))}</div>
              <div className="text-sm text-indigo-500 mt-2">jours</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-5 rounded-xl border-2 border-green-200">
              <div className="text-xs font-semibold text-green-500 uppercase tracking-wide mb-1">
                Tickets analysés
              </div>
              <div className="text-5xl font-bold text-green-700 leading-none">{allDays.length}</div>
              <div className="text-xs text-green-500 mt-2">jusqu'à {result.sampleSize} par sprint</div>
            </div>
          </div>

          {rows.every(r => r.count === 0) ? (
            <div className="text-center text-gray-400 text-sm py-6">
              Aucun ticket correspondant sur ces sprints.
            </div>
          ) : (
            <>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Temps en Test par sprint (jours)
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={rows} margin={{ top: 20, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#6b7280' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <Tooltip
                    cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                    formatter={(v, name) => [v == null ? '–' : `${v} j`, name]}
                    labelFormatter={(l, payload) => {
                      const p = payload?.[0]?.payload;
                      return p ? `Sprint ${l} · ${p.period} · ${p.count} tickets` : l;
                    }}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="average" name="Moyenne" fill="#3b82f6" radius={[5, 5, 0, 0]}>
                    <LabelList dataKey="average" position="top" style={{ fontSize: 11, fill: '#374151' }} />
                  </Bar>
                  <Line dataKey="median" name="Médiane" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>

              <table className="w-full text-sm mt-6">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-2 font-semibold">Sprint</th>
                    <th className="py-2 px-2 font-semibold">Période</th>
                    <th className="py-2 px-2 font-semibold text-right">Tickets analysés</th>
                    <th className="py-2 px-2 font-semibold text-right">Moyenne</th>
                    <th className="py-2 pl-2 font-semibold text-right">Médiane</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.sprint} className="border-b border-gray-100">
                      <td className="py-1.5 pr-2 text-gray-800">{r.label}</td>
                      <td className="py-1.5 px-2 text-gray-500">{r.period}</td>
                      <td className="py-1.5 px-2 text-right text-gray-600">
                        {r.count}
                        <span className="text-gray-400"> / {r.eligible} éligibles</span>
                        {r.eligible < 50 && (
                          <span className="ml-1 text-amber-600" title="Moins de 50 tickets éligibles : moyenne peu représentative">⚠</span>
                        )}
                      </td>
                      <td className="py-1.5 px-2 text-right font-semibold text-gray-900">{fmt(r.average)} j</td>
                      <td className="py-1.5 pl-2 text-right text-gray-600">{fmt(r.median)} j</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </div>
  );
}
